import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  inject,
  Input,
  NgZone,
  OnDestroy,
  ViewChild,
} from "@angular/core";
import { connectionFileTemplate } from "../../../core/generic-templates/formly-form-templates";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { NgbActiveModal, NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { FormlyBootstrapModule } from "@ngx-formly/bootstrap";
import { FormlyModule } from "@ngx-formly/core";
import _ from "lodash";
import {
  catchError,
  EMPTY,
  interval,
  Subject,
  Subscription,
  switchMap,
  takeUntil,
} from "rxjs";
import { INZorroOutput } from "../../../core/models/common";
import { RestService } from "../../../core/services/rest.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { MaterialModule } from "../../../core/shared/material.module";
import {
  validateApplicationDetail,
  validateApplicationsColumns,
  validatePageeColumn,
  validateSchemaDetail,
  validateServiceDetail,
  validateServiceOperationDetail,
  validateServiceParamDetail,
} from "../../../core/table-templates/validate-records-table";
import { recordedAppsTree } from "../../../core/tree/editor-tree";
import { MessageEnum } from "../../../core/utils/message.enum";
import { ZorroTableComponent } from "../../common/custom-table/zorro-table.component";
import { DragNDropComponent } from "../../common/drag-n-drop/drag-n-drop.component";
import { DynamicFormComponent } from "../../common/dynamic-form/dynamic-form.component";
import { LoadingComponent } from "../../common/loading/loading.component";
import { NoDataComponent } from "../../common/no-data/no-data.component";
import { ProcessLogEntry } from "../../common/process-logs-loader/process-log-entry";
import { ProcessLogsLoaderComponent } from "../../common/process-logs-loader/process-logs-loader.component";
import { TreeViewComponent } from "../../tree-view/tree-view.component";
import { FormlyModalComponent } from "../formly-modal/formly-modal.component";
import { ScriptValidationComponent, ScriptValidationItem } from "../script-validation/script-validation.component";
import { SwaggerValidationErrorModalComponent } from "../swagger-validation-error-modal/swagger-validation-error-modal.component";

@Component({
  selector: "app-validate-recorded-activities",
  standalone: true,
  imports: [
    CommonModule,
    TreeViewComponent,
    ZorroTableComponent,
    LoadingComponent,
    FormsModule,
    FormlyModule,
    FormlyBootstrapModule,
    ReactiveFormsModule,
    DynamicFormComponent,
    MaterialModule,
    NoDataComponent,
    DragNDropComponent,
    ProcessLogsLoaderComponent,
    FormlyModalComponent,
  ],
  templateUrl: "./validate-recorded-activities.component.html",
  styleUrl: "./validate-recorded-activities.component.scss",
})
export class ValidateRecordedActivitiesComponent implements AfterViewInit, OnDestroy {
  @Input()
  type: any = "";

  @Input()
  routeParams: any = {};

  @Input()
  meta: any = {};

  @Input()
  configs: any = {};

  @Input()
  fields: any = [];

  @Input()
  model: any = {};

  @Input()
  dataInput: any = {};

  @Input()
  isAgentic: boolean = false;

  @ViewChild(TreeViewComponent) treeViewComponent!: TreeViewComponent;
  @ViewChild(ZorroTableComponent) zorroTableComponent!: ZorroTableComponent;
  @ViewChild('storageConnectionForm') storageConnectionForm?: FormlyModalComponent;
  activeModal = inject(NgbActiveModal);

  dynamicForm: any = new FormGroup({});
  isActive:boolean = false;
  treeData: any = recordedAppsTree;
  selectedItem: any;
  tableData: any = [];
  tableOptions: any = { expandable: true };
  columns: any = {
    applications: validateApplicationsColumns,
    application: validateApplicationDetail,
    page: validatePageeColumn,
    service: validateServiceDetail,
    schema: validateSchemaDetail,
    operation: validateServiceOperationDetail,
    parameter: validateServiceParamDetail
  };
  selectedApplication: any;
  isLoading: boolean = false;
  validationCompletion: any = {};
  isDataLoaded = false;
  extentionId: any;
  responseCopy: any;
  totalItemsToValidate: number = 0;
  stopPolling$ = new Subject<void>();
  destroy$ = new Subject<void>();
  isSaving: boolean = false;
  displayLoader = false;
  noItemFoundMessage = "No item found";
  displayProgress = false;
  /** Prevents double-submit on Generate Scripts. */
  isGeneratingScripts = false;
  progressStatus = {
    text: 'Initializing',
    percentage: 2,
    isError: false
  };
  /** Live process logs from web (AI), mobile, and API task polling. */
  processLogs: ProcessLogEntry[] = [];
  /** True once API returns logs as an array (including empty []); false while undefined. */
  processLogsDefined = false;
  /** Latest raw `status` from poll response (e.g. PROCESSING_AUTOMATIC_SG). */
  private latestPollStatus = '';
  selectedFile: any;
  isDragOver: boolean = false;
  showResult = false;
  apiNodeTypes = ['command', 'schema', 'service', 'operation', 'parameter'];
  apiId: string = '';
  mobileData: any = {
    type: 'local'
  };
  connectionType = 'local';
  /** Storage Connections (FILE) — Local and S3 both live here. */
  storageConnections: any[] = [];
  filteredConnections: any[] = [];
  selectedConnections: {
    id: string;
    connectionId: string;
    name: string;
    remarks: string;
  }[] = [];
  connectionRemarksColumns: any[] = [];
  showAddConnectionForm = false;
  isSavingConnection = false;
  storageConnectionFields: any[] = [];
  storageConnectionConfigs: any = {};
  storageFormRenderKey = 0;
  private readonly storageFileAccept = [
    '.pdf',
    '.sql',
    '.xls',
    '.xlsx',
    '.doc',
    '.docx',
    '.csv',
  ];
  private connectionsLoaded = false;
  private readonly rootStyles = getComputedStyle(document.documentElement);

  private readonly constants: any = {
    PENDING_Agentic_LLM_TSG: 'Pending agentic LLM test script generation',
    PROCESSING_Agentic_LLM_TSG: 'Processing agentic LLM test script generation',
    COMPLETE_Agentic_LLM_TSG: 'Agentic LLM test script generation completed',
    ERROR_Agentic_LLM_TSG: 'Error while generating Agentic LLM test script',
    PENDING_AUTOMATIC_SG: 'Pending automatic script generation',
    PROCESSING_AUTOMATIC_SG: 'Processing automatic script generation',
    COMPLETE_AUTOMATIC_SG: 'Automatic script generation completed',
    ERROR_AUTOMATIC_SG: 'Error while generating automatic script',
    PENDING: 'Pending',
    PROCESSING: 'Processing',
    IN_PROGRESS: 'In progress',
    QUEUED: 'Queued',
    INITIALIZING: 'Initializing',
    COMPLETED: 'Process completed',
    COMPLETE: 'Process completed',
    FAILED: 'Failed',
    ERROR: 'Error',
  };

  private readonly modalService = inject(NgbModal);
  private readonly ngZone = inject(NgZone);

  recordingUrl = '';
  /** MANUAL generations can switch to the recording. Other modes stay on logs. */
  scriptGeneratorMode = '';
  /** Accello record id (`data.id`). The video URL must not use `taskId`. */
  private scriptRecordId = '';
  /** Latest polled `data.status` for the agentic script run. */
  private scriptRunStatus = '';
  /** Present once the recorder has a session. The video URL still uses `data.id`. */
  private scriptTaskId = '';
  private recordingRetry?: ReturnType<typeof setTimeout>;
  private recordingObjectUrl = '';
  /** Object URLs replaced by a newer frame, revoked once that frame has painted. */
  private retiredRecordingUrls: string[] = [];
  private recordingAbort?: AbortController;
  private recordingSub?: Subscription;
  /** Ignore a dropped stream until the user opens the video view again. */
  private recordingHold = false;
  private recordingToken = 0;
  /** AI flow: application details (Base URL, credentials) are being fetched after an app change. */
  isAppDetailsLoading = false;
  appDetailsError = '';
  /** Name of the application the Base URL was auto-filled from; cleared when the user edits it. */
  autoFilledFromApp = '';
  private readonly applicationDetailsRequest$ = new Subject<{
    projectId: string;
    automationProjectId: string;
    applicationId: string;
  }>();

  constructor(private readonly restService: RestService, private readonly toaster: ToasterService) { }

  ngOnInit() {
    this.listenForApplicationDetails();
    if(this.isAgentic && this.dataInput) {
      this.agenticDataFormat();
    }

    if(!this.isAgentic) {
      if (this.meta.type === "AI_Record") {
        this.displayProgress = true;
        this.processLogs = [];
        this.processLogsDefined = false;
        this.latestPollStatus = '';
        this.loadAIRecordActivities();
      } else if (this.type == "ai" || this.type === 'mobile') {
        this.tableData = this.configs?.tableData;
      }
      if (this.type === 'ai') {
        if (!this.model.appName) {
          this.model.appName = '';
        }
        this.refreshConnectionRemarksColumns();
      }
      if (this.type === 'mobile') {
        let apps = this.getApps();
        if (apps?.length) this.mobileData.application = apps[0].value;
        let devices = this.meta.devices?.filter((ele: any) => ele.environment.toLowerCase() === this.mobileData.type.toLowerCase());
        if (devices?.length) this.mobileData.device = devices[0].value;
      }
    }
  }

  ngAfterViewInit(): void {
    // NgbModal sets componentInstance inputs after open()/ngOnInit, so load here.
    this.tryLoadFileConnections();
  }

  agenticDataFormat() {
    if(this.type === 'web') {
      this.treeData[0].children = [];
      this.formatAndSetWebData(this.dataInput);
    } else if(this.type === 'mobile') {
      this.treeData[0].children = [];
      this.formatAndSetMobileData(this.dataInput);
    }
  }

  formatAndSetWebData(data: any) {
    this.responseCopy = data.responseAutomaticScriptGenerator;
          this.isLoading = false;
          this.isDataLoaded = true;
          let responseData = data?.responseAutomaticScriptGenerator;

          this.treeData[0].children.push({
            ...responseData?.automationApplication,
            displayName: responseData?.automationApplication?.name,
            icon: "sprint",
            order: 1,
            level: 2,
            nodeType: "application",
            isTickable: true,
            children: [],
          });

          responseData?.pages.forEach((page: any, ind: number) => {
            this.treeData[0].children[0].children.push({
              ...page,
              displayName: page.name,
              icon: "sprint",
              order: ind + 1,
              level: 3,
              nodeType: "page",
              isTickable: true,
            });
          });
          this.totalItemsToValidate = responseData?.pages?.length + 2;
  }

  formatAndSetMobileData(data: any) {
    this.responseCopy = data.response || data;
    this.isLoading = false;
    this.showResult = true;
    this.isDataLoaded = true;
    let responseData = data?.response || data;

    this.treeData[0].children.push({
      ...responseData?.automationApplication,
      displayName: responseData?.automationApplication?.name,
      icon: "sprint",
      order: 1,
      level: 2,
      nodeType: "application",
      isTickable: true,
      children: [],
    });

    responseData?.pages.forEach((page: any, ind: number) => {
      this.treeData[0].children[0].children.push({
        ...page,
        displayName: page.name,
        icon: "sprint",
        order: ind + 1,
        level: 3,
        nodeType: "page",
        isTickable: true,
      });
    });
    this.totalItemsToValidate = responseData?.pages?.length + 2;
    
    setTimeout(() => {
      this.selectedItem = this.treeData[0];
      this.onMenuItemSelected(this.treeData[0]);
    }, 500);
  }

  loadAIRecordActivities() {
    this.isLoading = true;
    this.treeData[0].children = [];
    interval(5000)
      .pipe(
        switchMap(() =>
          this.restService.getAIRecordedActivitiesStatus(
            this.routeParams.projectId,
            this.meta.aiRecordId
          )
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error(
            "An error occurred while loading recorded activities:",
            error
          );
          this.isLoading = false;
          this.isDataLoaded = false;
          this.toaster.pop({ type: 'error', text: MessageEnum.RECORDED_ACTIVITIES_LOAD_FAILURE });
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        this.latestPollStatus = res?.data?.status || '';
        this.updateProcessLogs(res?.data?.logs);
        this.updateRecording(res?.data);
        if (
          res.status === 201 &&
          res.data?.status !== 'COMPLETED'
        ) {
          if (res.data.status === 'ERROR_Agentic_LLM_TSG' || res.data.status === 'ERROR_AUTOMATIC_SG') {
            this.progressStatus.isError = true;
          } else {
            this.progressStatus.isError = false;
            if (this.progressStatus.text !== this.mapProcessStatus(res.data.status) && this.progressStatus.percentage < 90) {
              this.setProgressPercentage(Math.min(this.progressStatus.percentage + 20, 90));
            }
          }
          this.progressStatus.text = res.data.status === 'ERROR_Agentic_LLM_TSG' ? res.data.errorMessage : this.mapProcessStatus(res.data.status);
        } else {
          this.stopPolling$.next();
          const isCompleted = res.data?.status === 'COMPLETED' || res.data?.status === 'COMPLETE';
          this.progressStatus.text = isCompleted
            ? this.constants.COMPLETED
            : (res.data.status === 'ERROR_Agentic_LLM_TSG' ? res.data.errorMessage : this.mapProcessStatus(res.data.status) || this.constants.COMPLETED);
          this.progressStatus.percentage = 100;

          // Hide generation logs immediately before opening validation modal
          // so they don't flash under/after the nested modal.
          this.clearProcessLogsUI();

          this.responseCopy = res.data?.responseAutomaticScriptGenerator;
          this.isLoading = false;
          this.isDataLoaded = true;
          let responseData = res.data?.responseAutomaticScriptGenerator;

          this.treeData[0].children.push({
            ...responseData?.automationApplication,
            displayName: responseData?.automationApplication?.name,
            icon: "sprint",
            order: 1,
            level: 2,
            nodeType: "application",
            isTickable: true,
            children: [],
          });

          responseData?.pages.forEach((page: any, ind: number) => {
            this.treeData[0].children[0].children.push({
              ...page,
              displayName: page.name,
              icon: "sprint",
              order: ind + 1,
              level: 3,
              nodeType: "page",
              isTickable: true,
            });
          });
          this.totalItemsToValidate = responseData?.pages?.length + 2;
          this.openWebScriptValidation();
        }
      });
  }

  loadRecordedActivities() {
    this.isLoading = true;
    this.displayProgress = true;
    this.processLogs = [];
    this.processLogsDefined = false;
    this.latestPollStatus = '';
    this.progressStatus = {
      text: 'Initializing',
      percentage: 2,
      isError: false,
    };
    this.treeData[0].children = [];
    interval(5000)
      .pipe(
        switchMap(() =>
          this.restService.getRecordedActivities(
            this.routeParams.projectId,
            this.routeParams.automationId,
            this.routeParams.scriptId,
            { token: this.extentionId }
          )
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error(
            "An error occurred while loading recorded activities:",
            error
          );
          this.isLoading = false;
          this.isDataLoaded = false;
          this.progressStatus.isError = true;
          this.progressStatus.text = error?.message || MessageEnum.RECORDED_ACTIVITIES_LOAD_FAILURE;
          this.setProgressPercentage(100);
          this.toaster.pop({ type: 'error', text: MessageEnum.RECORDED_ACTIVITIES_LOAD_FAILURE });
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        const pollStatus = res?.data?.status || res?.data?.statusMessage || '';
        this.latestPollStatus = typeof pollStatus === 'string' ? pollStatus : '';
        this.updateProcessLogs(res?.data?.logs ?? res?.logs);
        this.updateRecording(res?.data ?? res);

        const stillGenerating =
          res.status === 200 &&
          res.async === false &&
          res.message === "Script is Generating, Try again later!";

        if (stillGenerating) {
          this.progressStatus.isError = false;
          if (this.progressStatus.percentage < 90) {
            this.setProgressPercentage(Math.min(this.progressStatus.percentage + 20, 90));
          }
          this.progressStatus.text =
            this.mapProcessStatus(this.latestPollStatus)
            || (typeof res?.data?.statusMessage === 'string' ? res.data.statusMessage : '')
            || 'Processing';
        } else {
          this.stopPolling$.next();
          this.progressStatus.isError = false;
          this.progressStatus.text = this.constants.COMPLETED;
          this.setProgressPercentage(100);

          // Hide generation logs before opening validation
          this.clearProcessLogsUI();

          this.responseCopy = res.data;
          this.isLoading = false;
          this.isDataLoaded = true;
          let responseData = res.data;

          this.treeData[0].children.push({
            ...responseData.automationApplication,
            displayName: responseData.automationApplication.name,
            icon: "sprint",
            order: 1,
            level: 2,
            nodeType: "application",
            isTickable: true,
            children: [],
          });

          responseData?.pages.forEach((page: any, ind: number) => {
            this.treeData[0].children[0].children.push({
              ...page,
              displayName: page.name,
              icon: "sprint",
              order: ind + 1,
              level: 3,
              nodeType: "page",
              isTickable: true,
            });
          });
          this.totalItemsToValidate = responseData?.pages?.length + 2;
          this.openWebScriptValidation();
        }
      });
  }

  saveValidations() {
    if(this.isAgentic) {
      this.activeModal.close();
      return;
    }
    this.isSaving = true;
    if (this.type === 'api') {
      this.saveApiValidations();
      return;
    }
    this.restService
      .saveRecordedActivities(
        this.routeParams.projectId,
        this.routeParams.automationId,
        this.routeParams.scriptId,
        this.getConstructedPayload()
      )
      .subscribe(
        (res: any) => {
          this.isSaving = false;
          const steps = res?.data?.steps ?? (Array.isArray(res?.data) ? res.data : null);
          if (res?.status === 200 || steps) {
            this.activeModal.close(steps ?? res?.data);
          } else {
            this.toaster.pop({type: 'error', text: MessageEnum.SOMETHING_WENT_WRONG });
            this.activeModal.dismiss();
          }
        },
        (error) => {
          this.isSaving = false;
          this.activeModal.dismiss();
          this.toaster.pop({type: 'error', text: error?.message || MessageEnum.SOMETHING_WENT_WRONG });
        }
      );
  }

  makeContextMenuVisible() {
    // Hide/Show context menu
    let ind = this.columns[this.selectedItem.nodeType].findIndex(
      (ele: any) => ele.key === "context_menu"
    );
    if (this.columns[this.selectedItem.nodeType][ind])
      this.columns[this.selectedItem.nodeType][ind].hidden =
        this.validationCompletion[this.selectedItem.id];
  }

  onMenuItemSelected(node: any) {
    if (this.type === 'api' && !this.apiNodeTypes.includes(node.nodeType)) {
      return;
    }
    this.selectedItem = node;
    this.makeContextMenuVisible();
    if (node.nodeType === "page") {
      let uiObjects = _.cloneDeep(node.uiObjects);
      uiObjects.forEach((obj: any) => {
        obj.children = obj.children || obj.locators;
      });
      this.tableData = uiObjects;
    } else if (this.apiNodeTypes.includes(node.nodeType)) {
      if (node.nodeType === 'schema') {
        this.tableData = _.cloneDeep(node.attributes);
      } else if (node.nodeType === 'parameter') {
        this.tableData = _.cloneDeep(node.value);
      } else {
        this.tableData = [_.cloneDeep(node)];
      }
      setTimeout(() => {
        this.updateTable();
      }, 100);
    } else {
      this.tableData = _.cloneDeep(node.children);
    }
  }

  updateTable() {
    this.zorroTableComponent?.refreshTableData();
  }

  /**
   * Displays a toaster notification.
   * @param message The message to display.
   * @param isSuccess Whether the notification indicates success.
   * @param showDefaultError Whether to display a default error message.
   */
  popToaster(message: string, isSuccess = true, showDefaultError?: boolean) {
    if (isSuccess) {
      this.toaster.pop({ type: 'success', text: message });
    } else {
      this.toaster.pop({ type: 'error', text: showDefaultError ? MessageEnum.SOMETHING_WENT_WRONG : message });
    }
  }

  onTableActinChange(event: INZorroOutput) {
    let rowId = event.data.row.id;
    if (event.type === "contextMenu" && event.value === "edit") {
      this.makeRowEditableById(rowId, this.tableData);
    } else if (event.type === "contextMenu" && event.value === "remove") {
      this.deleteRowById(rowId, this.tableData);
    }
  }

  deleteRowById(id: string, list: any[]) {
    let isResolved = false;
    list.forEach((ele: any, ind: number) => {
      if (ele.id === id) {
        list.splice(ind, 1);
        isResolved = true;
      }
      if (!isResolved && ele.children?.length) {
        this.deleteRowById(id, ele.children);
      }
    });
    if (isResolved) this.zorroTableComponent.refreshTableData();
  }

  makeRowEditableById(id: string, list: any[]) {
    let isResolved = false;
    list.forEach((ele: any, ind: number) => {
      if (ele.id === id) {
        isResolved = true;
        list[ind].isRowEditable = true;
      }
      if (!isResolved && ele.children?.length) {
        this.makeRowEditableById(id, ele.children);
      }
    });
    if (isResolved) this.zorroTableComponent.refreshTableData();
  }

  markAllRowsNonEditable(list: any[]) {
    list.forEach((ele: any, ind: number) => {
      delete ele.isRowEditable;
      if (ele.children?.length) {
        this.markAllRowsNonEditable(ele.children);
      }
    });
  }

  completeValidation(event: any) {
    this.markTreeNode(
      this.selectedItem.id,
      this.treeData,
      this.validationCompletion[this.selectedItem.id] ? true : false,
      true
    );
    this.makeContextMenuVisible();
    if (this.validationCompletion[this.selectedItem.id]) {
      if (this.type === 'api') {
        this.updateApiTreeNode(this.treeData, this.selectedItem.id, this.tableData);
      } else {
        this.updateTreeNode(this.treeData, this.selectedItem.id, this.tableData);
      }
      this.markAllRowsNonEditable(this.tableData);
      setTimeout(() => {
        this.zorroTableComponent.refreshTableData();
      }, 50);
    }
  }

  updateTreeNode(arr: any[], targetId: string, data: any) {
    let isResolved = false;
    arr.forEach((ele: any, ind: number) => {
      if (ele.id === targetId) {
        if (this.selectedItem.nodeType === "page") {
          arr[ind].uiObjects = data;
        } else {
          data.forEach((child: any) => (child.displayName = child.name));
          if (arr[ind].children?.length) arr[ind].children = data;
        }
        isResolved = true;
      }
      if (!isResolved && ele.children?.length) {

        this.updateTreeNode(ele.children, targetId, data);
      }
    });
  }

  updateApiTreeNode(arr: any[], targetId: string, data: any) {
    let isResolved = false;
    arr.forEach((ele: any, ind: number) => {
      if (ele.id === targetId) {
        switch (this.selectedItem.nodeType) {
          case 'service':
            arr[ind].apiDefinitionId = data[0].apiDefinitionId;
            arr[ind].url = data[0].url;
            arr[ind].name = data[0].name;
            arr[ind].displayName = data[0].displayName;
            break;
          case 'operation':
            arr[ind].acceptType = data[0].acceptType;
            arr[ind].type = data[0].type;
            arr[ind].name = data[0].name;
            arr[ind].complexReturnType = data[0].complexReturnType;
            arr[ind].returnType = data[0].returnType;
            arr[ind].url = data[0].url;
            break;
          case 'parameter':
            arr[ind].paramType = data[0].paramType;
            arr[ind].contentType = data[0].contentType;
            arr[ind].payloadType = data[0].payloadType;
            arr[ind].type = data[0].type;
            arr[ind].name = data[0].name;
            break;
          case 'schema':
            arr[ind].attributes = data;
            break;
        }
        isResolved = true;
      }
      if (!isResolved && ele.children?.length) {
        this.updateApiTreeNode(ele.children, targetId, data);
      }
    });
  }

  markTreeNode(id: string, list: any[], value: boolean, markAll?: boolean) {
    let isResolved = false;
    list.forEach((ele: any, ind: number) => {
      if (ele.id === id) {
        isResolved = true;
        list[ind].isTicked = value;
        if (markAll) this.markChildNodes(list[ind].children, value);
      }
      if (!isResolved && ele.children?.length) {
        this.markTreeNode(id, ele.children, value, markAll);
      }
    });
  }

  markChildNodes(list: any[], value: boolean) {
    list?.forEach((ele: any) => {
      if (ele.isTickable) {
        ele.isTicked = value;
        this.validationCompletion[ele.id] = value;
      }
      if (ele.children?.length) {
        this.markChildNodes(ele.children, value);
      }
    });
  }

  getConstructedPayload() {
    const payload = this.responseCopy;
    // Update Application
    let application = _.cloneDeep(this.treeData[0].children[0]);
    payload.automationApplication = {
      browser: application.browser,
      name: application.name,
      paradigm: application.paradigm,
    };
    if(this.type === 'mobile' && application.id) {
      payload.automationApplication.id = application.id;
    }
    // Update Page/UiObjects
    let pages = _.cloneDeep(this.treeData[0].children[0].children);
    pages.forEach((ele: any) => {
      delete ele.displayName;
      delete ele.isTickable;
      delete ele.isTicked;
      delete ele.order;
      delete ele.level;
      delete ele.icon;
      ele?.uiObjects.forEach((obj: any) => delete obj.children);
    });
    payload.pages = pages;
    return payload;
  }

  getProgress() {
    return `${(Object.keys(this.validationCompletion).filter(
      (key) => this.validationCompletion[key]
    ).length /
      this.totalItemsToValidate) *
      100
      }%`;
  }

  ngOnDestroy() {
    this.endRecordingStream(true);
    this.destroy$.next();
    this.destroy$.complete();
    this.stopPolling$.complete();
  }

  handleTableActions(event: INZorroOutput) {
    console.log(event);
    switch (event?.actionType) {
      case "add":
        this.addDataToTable(event);
        break;
      case "cancel":
        this.clearFields(event);
        break;
      case "edit":
        this.setCredentialRowEditable(event?.data?.row);
        break;
      case "delete":
        this.deleteCredentialRow(event?.data?.row);
        break;
      default:
        break;
    }
  }

  // The zorro table renders (and binds inputs to) copies of tableData.rows, and `event.data.row`
  // is that copy. Rows are matched by id and both objects are updated in place, so the table
  // isn't rebuilt and unsaved values in other rows that are still being edited are kept.
  private findCredentialRow(row: any): any {
    return this.tableData.rows.find((item: any) => item.id === row?.id);
  }

  setCredentialRowEditable(row: any) {
    const originalRow = this.findCredentialRow(row);
    if (!originalRow) return;
    originalRow.isRowEditable = true;
    row.isRowEditable = true;
  }

  deleteCredentialRow(row: any) {
    if (!this.findCredentialRow(row)) return;
    // New array reference so the table picks up the removal
    this.tableData.rows = this.tableData.rows.filter((item: any) => item.id !== row.id);
  }

  addDataToTable(event: any) {
    const row = event?.data?.row;
    const originalRow = this.findCredentialRow(row);

    const requiredFields = [
      row?.username,
      row?.password
    ];

    const allFieldsFilled = requiredFields.every(
      (field) => typeof field === "string" && field.trim() !== ""
    );

    if (originalRow && allFieldsFilled) {
      Object.assign(originalRow, {
        role: row.role,
        username: row.username,
        password: row.password,
        remarks: row.remarks,
        isRowEditable: false,
        isDraft: false,
      });
      row.isRowEditable = false;
      row.isDraft = false;
    }
  }

  clearFields(event: any) {
    const row = event?.data?.row;
    const originalRow = this.findCredentialRow(row);
    if (!originalRow) return;

    if (originalRow.isDraft) {
      // New row: clear what was typed and keep it open for input
      Object.assign(row, { role: "", username: "", password: "", remarks: "" });
      return;
    }

    // Existing row: discard unsaved changes and leave edit mode
    Object.assign(row, {
      role: originalRow.role,
      username: originalRow.username,
      password: originalRow.password,
      remarks: originalRow.remarks,
      isRowEditable: false,
    });
    originalRow.isRowEditable = false;
  }

  loadActivities() {
    if (this.isGeneratingScripts || this.isButtonDisabled()) {
      return;
    }
    this.isGeneratingScripts = true;

    if (this.type === 'api') {
      this.uploadAPISwagger();
      return;
    }

    if (this.type === 'mobile') {
      this.createTask();
      return;
    }

    if(this.type === 'ai') {
      const credentials = (this.tableData?.rows || [])
        .filter((item: any) => !item.isDraft && item.username && item.password)
        .map((item: any) => ({
          role: item.role,
          username: item.username,
          password: item.password,
          remarks: item.remarks
        }));
      const files = this.buildSelectedFilesPayload();
      const selectedApp = this.getApplicationOptions().find(
        (opt: any) => (opt.id || opt.value) === this.model.appName
      );
      const requestObj: any = {
        projectId: this.routeParams.projectId,
        applicationName: selectedApp?.name || selectedApp?.label || 'sampleApp',
        applicationUrl: this.model.baseUrl,
        automationProjectId: this.routeParams.automationId,
        automationApplicationId: this.model.appName,
        automationScriptId: this.routeParams.scriptId,
        testCaseId: this.model.testCaseId || '',
        credentials
      };
      if (files.length) {
        requestObj.files = files;
      }
      this.displayProgress = true;
      this.progressStatus.text = 'Generating scripts';
      this.progressStatus.percentage = 2;
      this.progressStatus.isError = false;
      this.processLogs = [];
      this.processLogsDefined = false;
      this.latestPollStatus = '';
      this.restService.generateScriptsByLLM(requestObj).subscribe({
        next: (response: any) => {
          this.meta.aiRecordId = response?.data?.id;
          this.updateRecording(response?.data);
          this.loadAIRecordActivities();
        },
        error: (err: any) => {
          this.isGeneratingScripts = false;
          this.displayProgress = false;
          this.progressStatus.text = err?.message || 'Error occurred while generating scripts';
          this.progressStatus.isError = true;
        }
      });
      return;
    }

    this.model.tableData = this.tableData.rows;
    this.activeModal.close({
      value: this.model,
      fields: this.fields,
    });
  }

  createTask() {
    let payload = {
      "automationProjectId": this.routeParams.automationId,
      "automationApplicationId": this.mobileData.application,
      "automationScriptId": this.routeParams.scriptId,
      "testCaseId": this.model.testCaseId,
      // "environmentType": 'LOCAL',
      "deviceId": this.mobileData.device,
      "credentials": this.tableData.rows.filter((ele: any) => !ele.isDraft)?.map((item: any) => {
        return {
          role: item.role,
          username: item.username,
          password: item.password,
          remarks: item.remarks
        }
      })
    };
    this.restService.createMobileTask(this.routeParams.projectId, payload).subscribe((res: any) => {
      if (res?.data?.taskId) {
        this.pollMobileTask(res.data.id);
      } else {
        this.isGeneratingScripts = false;
      }
    }, error => {
      this.isGeneratingScripts = false;
      this.toaster.pop({ type: 'error', text: error.message || MessageEnum.SOMETHING_WENT_WRONG }, { backgroundColor: 'transparent' });
    });
  }

  pollMobileTask(taskId: string) {
    this.isLoading = true;
    this.displayProgress = true;
    this.processLogs = [];
    this.processLogsDefined = false;
    this.latestPollStatus = '';
    this.progressStatus = {
      text: 'Initializing',
      percentage: 2,
      isError: false
    };
    this.treeData[0].children = [];
    interval(5000)
      .pipe(
        switchMap(() =>
          this.restService.getMobileTaskDetail(
            this.routeParams.projectId,
            taskId
          )
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error(
            "An error occurred while loading recorded activities:",
            error
          );
          this.isLoading = false;
          this.isDataLoaded = false;
          this.displayProgress = false;
          this.toaster.pop({ type: 'error', text: MessageEnum.ERROR_WHILE_RETRIEVING_MOBILE_TASK });
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        this.latestPollStatus = res?.data?.status || '';
        this.updateProcessLogs(res?.data?.logs);
        this.updateRecording(res?.data);
        const taskStatus = String(res?.data?.status || '').toUpperCase();
        const isCompleted = taskStatus === 'COMPLETED' || taskStatus === 'COMPLETE';
        const isFailed = taskStatus === 'FAILED' || taskStatus === 'ERROR';

        if (!isCompleted && !isFailed && (res.status === 201 || taskStatus === 'PROCESSING' || taskStatus === 'PENDING')) {
          this.progressStatus.isError = false;
          if (this.progressStatus.percentage < 90) {
            this.setProgressPercentage(Math.min(this.progressStatus.percentage + 20, 90));
          }
          this.progressStatus.text = res.data.statusMessage || this.mapProcessStatus(res.data.status) || 'Processing';
        } else if (isFailed) {
          this.progressStatus.isError = true;
          this.progressStatus.text = res.data.response?.data || res.data.error || res.data.statusMessage || 'Task failed';
          this.setProgressPercentage(100);
          this.stopPolling$.next();
        } else {
          this.stopPolling$.next();
          this.progressStatus.isError = false;
          this.progressStatus.text = this.constants.COMPLETED;
          this.progressStatus.percentage = 100;

          // Hide generation logs immediately before opening validation modal
          this.clearProcessLogsUI();

          this.responseCopy = res.data?.response;
          this.isLoading = false;
          this.showResult = true;
          this.isDataLoaded = true;
          let responseData = res.data?.response;

          this.treeData[0].children.push({
            ...responseData?.automationApplication,
            displayName: responseData?.automationApplication?.name,
            icon: "sprint",
            order: 1,
            level: 2,
            nodeType: "application",
            isTickable: true,
            children: [],
          });

          responseData?.pages.forEach((page: any, ind: number) => {
            this.treeData[0].children[0].children.push({
              ...page,
              displayName: page.name,
              icon: "sprint",
              order: ind + 1,
              level: 3,
              nodeType: "page",
              isTickable: true,
            });
          });
          this.totalItemsToValidate = responseData?.pages?.length + 2;
          this.openMobileScriptValidation();
        }
      });
  }

  onButtonClick() {
    if (this.tableData.rows?.length && this.tableData.rows[0].isDraft) {
      return;
    }
    const obj = {
      role: "",
      username: "",
      password: "",
      remarks: "",
      isRowEditable: true,
      isDraft: true,
    };
    this.tableData.rows = [obj, ...this.tableData.rows];
  }

  isButtonDisabled(): boolean {
    if (this.isGeneratingScripts || this.isLoading || this.displayProgress) {
      return true;
    }
    if (this.type === 'api' && !this.selectedFile) {
      return true;
    }
    if (this.type === 'ai') {
      return this.isAppDetailsLoading || !this.model.appName || !this.model.baseUrl?.trim();
    }
    const hasEmptyInput = this.fields
      .filter((field: any) => field.type === 'text-input')
      .some((field: any) => !this.model[field.key]?.trim());

    // const hasEmptyPassword = this.tableData?.rows?.some((row:any) => !row.password?.trim()); //not a mandatory

    if (hasEmptyInput) {
      return true;
    } else {
      return false;
    }
  }

  getApplicationOptions(): any[] {
    return (
      this.fields?.find((field: any) => field.key === 'appName')?.options ||
      this.meta?.application ||
      []
    );
  }

  private connectionLoadAttempts = 0;

  tryLoadFileConnections(): void {
    if (this.connectionsLoaded) {
      return;
    }
    // NgbModal assigns type/routeParams after open(); retry briefly until ready
    if (this.type !== 'ai' || !this.routeParams?.projectId) {
      if (this.connectionLoadAttempts < 10) {
        this.connectionLoadAttempts += 1;
        setTimeout(() => this.tryLoadFileConnections(), 50);
      }
      return;
    }
    this.loadFileConnections();
  }

  onApplicationChange(): void {
    // Ensure storage connections are loaded once the form is in use
    if (!this.connectionsLoaded) {
      this.connectionLoadAttempts = 0;
      this.tryLoadFileConnections();
    } else {
      this.applyConnectionFilter();
    }
    this.loadSelectedApplicationDetails();
  }

  retryApplicationDetails(): void {
    this.loadSelectedApplicationDetails();
  }

  /** Clears the "Auto-filled from …" note once the user edits the Base URL. */
  onBaseUrlChange(): void {
    this.autoFilledFromApp = "";
  }

  private loadSelectedApplicationDetails(): void {
    const applicationId = this.model?.appName;
    const projectId = this.routeParams?.projectId;
    const automationProjectId = this.routeParams?.automationId;
    if (
      this.type !== "ai" ||
      !applicationId ||
      !projectId ||
      !automationProjectId
    ) {
      return;
    }

    this.applicationDetailsRequest$.next({
      projectId,
      automationProjectId,
      applicationId,
    });
  }

  // switchMap drops an in-flight request when the user picks another application,
  // so a slow response for the previous app can't overwrite the current one.
  private listenForApplicationDetails(): void {
    this.applicationDetailsRequest$
      .pipe(
        switchMap(({ projectId, automationProjectId, applicationId }) => {
          const appName = this.getApplicationName(applicationId);
          this.isAppDetailsLoading = true;
          this.appDetailsError = "";
          this.autoFilledFromApp = "";
          // Don't leave the previous application's values next to the new name while loading
          this.model.baseUrl = "";
          this.applyApplicationCredentials([]);

          return this.restService
            .getAutomationApplicationsById(projectId, automationProjectId, applicationId)
            .pipe(
              catchError((error: any) => {
                this.isAppDetailsLoading = false;
                this.appDetailsError = error?.message || `Couldn't load ${appName} details.`;
                return EMPTY;
              })
            );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res: any) => {
        const data = res?.data ?? res ?? {};
        this.model.baseUrl =
          data.applicationUrl ?? data.baseUrl ?? data.url ?? "";
        this.applyApplicationCredentials(data.credentials);
        this.autoFilledFromApp = this.model.baseUrl
          ? this.getApplicationName(this.model.appName)
          : "";
        this.isAppDetailsLoading = false;
      });
  }

  private getApplicationName(applicationId: string): string {
    const app = this.getApplicationOptions().find(
      (opt: any) => (opt.id || opt.value) === applicationId
    );
    return app?.name || app?.label || "the application";
  }

  private applyApplicationCredentials(rawCredentials: any): void {
    let credentials = rawCredentials;
    if (typeof credentials === "string") {
      try {
        credentials = JSON.parse(credentials);
      } catch {
        credentials = [];
      }
    }
    if (!Array.isArray(credentials)) {
      credentials = [];
    }

    const columns =
      this.tableData?.columns || this.configs?.tableData?.columns || [];
    this.tableData = {
      ...(this.tableData || {}),
      columns,
      rows: credentials.map((item: any, index: number) => ({
        id: item?.id ?? `cred-${index + 1}`,
        role: item?.role ?? "",
        username: item?.username ?? "",
        password: item?.password ?? "",
        remarks: item?.remarks ?? "",
        isRowEditable: false,
        isDraft: false,
      })),
    };
  }

  loadFileConnections(autoSelectId?: string): void {
    const projectId = this.routeParams?.projectId;
    if (!projectId || this.type !== 'ai') {
      return;
    }
    // Storage Connections = data.FILE (Local + S3); filter by meta.type
    this.restService.getConnections(projectId).subscribe({
      next: (res: any) => {
        this.connectionsLoaded = true;
        const data = res?.data ?? {};
        this.storageConnections = Array.isArray(data.FILE) ? data.FILE : [];
        this.applyConnectionFilter();
        if (autoSelectId) {
          const exists = this.filteredConnections.some(
            (conn: any) => this.getConnectionId(conn) === autoSelectId
          );
          if (exists) {
            this.addConnectionSelection(autoSelectId);
          }
        }
      },
      error: () => {
        this.connectionsLoaded = false;
        this.storageConnections = [];
        this.filteredConnections = [];
        this.refreshConnectionRemarksColumns();
      },
    });
  }

  private createConnectionRowKey(): string {
    return `conn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  }

  addConnectionRow(): void {
    const rowId = this.createConnectionRowKey();
    this.selectedConnections = [
      ...this.selectedConnections,
      {
        id: rowId,
        connectionId: "",
        name: "",
        remarks: "",
      },
    ];
  }

  addConnectionSelection(connectionId: string, remarks = ""): void {
    if (!connectionId) {
      return;
    }
    if (
      this.selectedConnections.some((item) => item.connectionId === connectionId)
    ) {
      return;
    }
    const conn =
      this.storageConnections.find(
        (item: any) => this.getConnectionId(item) === connectionId
      ) ||
      this.filteredConnections.find(
        (item: any) => this.getConnectionId(item) === connectionId
      );
    this.selectedConnections = [
      ...this.selectedConnections,
      {
        id: this.createConnectionRowKey(),
        connectionId,
        name: this.getConnectionLabel(conn) || connectionId,
        remarks,
      },
    ];
  }

  removeSelectedConnectionRow(rowId: string): void {
    this.selectedConnections = this.selectedConnections.filter(
      (item) => item.id !== rowId
    );
  }

  handleConnectionTableActions(event: INZorroOutput): void {
    const row = event?.data?.row;
    if (event?.actionType === "delete") {
      if (row?.id) {
        this.removeSelectedConnectionRow(row.id);
      }
      return;
    }
    if (event?.type === "select" && row) {
      const connectionId = (row.connectionId || "").toString();
      const conn = this.filteredConnections.find(
        (item: any) => this.getConnectionId(item) === connectionId
      );
      const name = this.getConnectionLabel(conn) || connectionId;
      row.name = name;
      const target = this.selectedConnections.find((item) => item.id === row.id);
      if (target) {
        target.connectionId = connectionId;
        target.name = name;
      }
    }
  }

  private refreshConnectionRemarksColumns(): void {
    const options = [
      { value: "", label: "Select file connection" },
      ...this.filteredConnections.map((conn: any) => ({
        value: this.getConnectionId(conn),
        label: this.getConnectionLabel(conn),
      })),
    ];
    this.connectionRemarksColumns = [
      {
        key: "connectionId",
        display_name: "File Connection",
        cellStyles: { "font-size": "13px" },
        headerStyles: {
          background: "#7593F2",
          "font-weight": "600",
          width: "35%",
          borderRadius: "6px 0 0",
          color: "#fff",
        },
        _config: {
          type: "select",
          options,
          classNames: "border-1 bg-white",
        },
      },
      {
        key: "remarks",
        display_name: "Remarks",
        cellStyles: { "font-size": "13px" },
        headerStyles: {
          background: "#7593F2",
          "font-weight": "600",
          color: "#fff",
        },
        _config: {
          type: "input",
          placeholder: "Add remarks for this connection",
          classNames: "border-1 bg-white",
        },
      },
      {
        key: "delete",
        display_name: "",
        cellStyles: { "font-size": "13px" },
        headerStyles: {
          background: "#7593F2",
          borderRadius: "0 6px 0 0",
          "font-weight": "600",
          width: "3%",
          color: "#fff",
        },
        _config: {
          type: "svg-editor",
          value: "../../../../assets/images/virtusa-icons/delete-light.svg",
          isClickable: "true",
          actionType: "delete",
        },
      },
    ];
  }

  openAddConnectionForm(): void {
    this.prepareStorageConnectionForm();
    this.showAddConnectionForm = true;
  }

  cancelAddConnectionForm(): void {
    this.showAddConnectionForm = false;
    this.isSavingConnection = false;
  }

  private prepareStorageConnectionForm(): void {
    this.storageConnectionFields = connectionFileTemplate(null, this.rootStyles);
    this.storageConnectionConfigs = {
      title: 'Add New Storage Connection',
      saveBtnText: 'Add',
      isEdit: false,
      model: { type: this.connectionType || 'local' },
      predefinedTags: [],
      allowedFileExtensions: this.storageFileAccept,
      fileAcceptByTarget: {
        testDataGeneration: this.storageFileAccept,
        testCaseGenerator: this.storageFileAccept,
      },
    };
    this.storageFormRenderKey += 1;
  }

  applyConnectionFilter(): void {
    this.filteredConnections = this.dedupeConnections(
      this.storageConnections.filter((conn: any) => this.isActiveConnection(conn))
    );
    this.refreshConnectionRemarksColumns();
  }

  getConnectionId(conn: any): string {
    // Storage FILE connections use `id` (not `_id`)
    return (conn?.id || conn?._id || conn?.connectionId || conn?.sourceId || '').toString();
  }

  getConnectionLabel(conn: any): string {
    return (
      conn?.connectionName ||
      conn?.meta?.fileName ||
      conn?.name ||
      this.getConnectionId(conn)
    );
  }

  private isActiveConnection(conn: any): boolean {
    if (conn?.active === false) {
      return false;
    }
    const status = (conn?.connectionStatus || '').toString().toUpperCase();
    return !status || status === 'ACTIVE';
  }

  private dedupeConnections(connections: any[]): any[] {
    const seen = new Set<string>();
    return connections.filter((conn: any) => {
      const key = this.getConnectionId(conn) || this.getConnectionLabel(conn);
      if (!key || seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  buildSelectedFilesPayload(): { id: string; remarks: string }[] {
    const seen = new Set<string>();
    return (this.selectedConnections || [])
      .filter((item) => {
        const connectionId = (item.connectionId || "").toString().trim();
        if (!connectionId || seen.has(connectionId)) {
          return false;
        }
        seen.add(connectionId);
        return true;
      })
      .map((item) => ({
        id: item.connectionId,
        remarks: (item.remarks || "").trim(),
      }));
  }

  saveInlineConnection(): void {
    if (this.isSavingConnection || !this.storageConnectionForm?.canSubmit) {
      return;
    }
    this.storageConnectionForm.onSubmit();
  }

  onStorageConnectionSaved(result: any): void {
    const model = result?.value;
    const projectId = this.routeParams?.projectId;
    if (!model || !projectId || this.isSavingConnection) {
      return;
    }

    this.isSavingConnection = true;
    const type = (model.type || this.connectionType || 'local').toLowerCase();

    if (type === 'local') {
      const formData = new FormData();
      const file = model.fileUpload as File;
      formData.append('type', 'local');
      formData.append('name', model.connectionName || file?.name || '');
      if (model.targetAsset) {
        formData.append('targetApplications', model.targetAsset);
      }
      if (model.tags) {
        formData.append('tags', model.tags);
      }
      if (file) {
        formData.append('file', file, file.name);
      }

      this.restService.createFileConnection(projectId, formData).subscribe({
        next: (res: any) => this.onConnectionCreated(res?.data, 'local'),
        error: (error: any) => {
          this.isSavingConnection = false;
          this.toaster.pop({
            type: 'error',
            text: error?.message || MessageEnum.CONNECTION_CREATE_FAILURE,
          });
        },
      });
      return;
    }

    const payload = {
      connectionName: model.connectionName,
      connectionType: 'FILE',
      targetApplications: model.targetAsset,
      tags: model.tags,
      type: 's3',
      accessKeyID: model.accessKeyID,
      accessKeySecret: model.accessKeySecret,
      s3BucketName: model.s3BucketName,
      region: model.region,
    };

    this.restService.createConnection(projectId, payload).subscribe({
      next: (res: any) => this.onConnectionCreated(res?.data, 's3'),
      error: (error: any) => {
        this.isSavingConnection = false;
        this.toaster.pop({
          type: 'error',
          text: error?.message || MessageEnum.CONNECTION_CREATE_FAILURE,
        });
      },
    });
  }

  private onConnectionCreated(created: any, type: 'local' | 's3'): void {
    const newId = this.getConnectionId(created);
    this.toaster.pop({
      type: 'success',
      text:
        type === 'local'
          ? MessageEnum.FILE_UPLOAD_SUCCESS
          : MessageEnum.CONNECTION_CREATE_SUCCESS,
    });
    this.isSavingConnection = false;
    this.showAddConnectionForm = false;
    this.connectionType = type;
    this.connectionsLoaded = false;

    if (created && newId) {
      this.storageConnections = this.dedupeConnections([
        created,
        ...this.storageConnections,
      ]);
      this.applyConnectionFilter();
      this.addConnectionSelection(newId);
    }

    this.loadFileConnections(newId);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.selectedFile = Array.from(files)[0];
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input && input.files) {
      this.selectedFile = Array.from(input.files)[0];
    }
  }

  private parentModalWindow?: HTMLElement;
  private parentModalBackdrop?: HTMLElement;

  /** Keep the generation popup hidden once validation is open, including after Save. */
  private hideParentModal(): void {
    if (!this.parentModalWindow) {
      const windows = document.querySelectorAll('ngb-modal-window');
      const parent = windows.item(windows.length - 1) as HTMLElement | null;
      if (!parent) {
        return;
      }
      this.parentModalWindow = parent;
      const backdrop = parent.previousElementSibling as HTMLElement | null;
      if (backdrop?.classList.contains('modal-backdrop')) {
        this.parentModalBackdrop = backdrop;
      }
    }
    this.parentModalWindow.style.setProperty('display', 'none', 'important');
    this.parentModalBackdrop?.style.setProperty('display', 'none', 'important');
  }

  /** Show the generation popup again with the saving state until commands are loaded. */
  private showSavingProgress(): void {
    this.parentModalWindow?.style.removeProperty('display');
    this.parentModalBackdrop?.style.removeProperty('display');
    this.clearProcessLogsUI();
    this.displayProgress = true;
    this.progressStatus = {
      text: 'Saving validated activities...',
      percentage: 80,
      isError: false,
    };
  }

  openWebScriptValidation(): void {
    const pages: any[] = this.treeData[0]?.children[0]?.children || [];

    const allPageItems: ScriptValidationItem[] = pages.map((page: any) => ({
      id: page.id,
      name: page.name,
      url: page.url,
      validated: false,
      enabled: false,
      expanded: false,
      fields: (page.uiObjects || []).map((obj: any) => ({
        name: obj.name,
        locator: obj.locators?.[0]?.locator || '',
        type: obj.locators?.[0]?.type || ''
      }))
    }));

    const svTreeNodes = [{
      id: 'pages',
      displayName: 'Pages',
      count: pages.length,
      children: pages.map((page: any) => ({ id: page.id, displayName: page.name }))
    }];

    const nodeDataMap: { [key: string]: ScriptValidationItem[] } = {};
    pages.forEach((page: any) => {
      nodeDataMap[page.id] = allPageItems;
    });

    this.hideParentModal();
    const svModalRef = this.modalService.open(ScriptValidationComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'scrip-mapping-modal'
    });
    svModalRef.componentInstance.treeNodes = svTreeNodes;
    svModalRef.componentInstance.nodeDataMap = nodeDataMap;
    svModalRef.componentInstance.type = 'web';

    svModalRef.result.then(() => {
      this.showSavingProgress();
      this.saveValidations();
    }, () => {
      this.activeModal.dismiss();
    });
  }


  openMobileScriptValidation(): void {
    const pages: any[] = this.treeData[0]?.children[0]?.children || [];

    const allPageItems: ScriptValidationItem[] = pages.map((page: any) => ({
      id: page.id,
      name: page.name,
      url: page.url,
      validated: false,
      enabled: false,
      expanded: false,
      fields: (page.mobileObjects || page.uiObjects || []).map((obj: any) => ({
        name: obj.name,
        dataType: obj.locators?.[0]?.locator || obj.locator || ''
      }))
    }));

    const svTreeNodes = [{
      id: 'pages',
      displayName: 'Pages',
      count: pages.length,
      children: pages.map((page: any) => ({ id: page.id, displayName: page.name }))
    }];

    const nodeDataMap: { [key: string]: ScriptValidationItem[] } = {};
    pages.forEach((page: any) => {
      nodeDataMap[page.id] = allPageItems;
    });

    this.hideParentModal();
    const svModalRef = this.modalService.open(ScriptValidationComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'scrip-mapping-modal'
    });
    svModalRef.componentInstance.treeNodes = svTreeNodes;
    svModalRef.componentInstance.nodeDataMap = nodeDataMap;
    svModalRef.componentInstance.type = 'mobile';

    svModalRef.result.then(() => {
      this.showSavingProgress();
      this.saveValidations();
    }, () => {
      this.activeModal.dismiss();
    });
  }

  openSwaggerValidationError(errorMessage: string): void {
    const modalRef = this.modalService.open(SwaggerValidationErrorModalComponent, {
      centered: true,
      size: 'lg',
      windowClass: 'add-item-modal',
      backdrop: 'static',
    });
    modalRef.componentInstance.errorMessage = errorMessage;
  }

  triggerFileInput(): void {
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    fileInput?.click();
  }

  uploadAPISwagger() {

     if(this.isAgentic) {
      this.activeModal.close({ file: this.selectedFile, mappedTestCase: this.model.testCase });
      return;
    }
    
    let formData = new FormData();
    formData.append("testCaseId", this.model.testCaseId);
    formData.append('file', this.selectedFile);
    formData.append('automationProjectId', this.routeParams.automationId);
    formData.append('projectId', this.routeParams.projectId);
    formData.append('automationScriptId', this.routeParams.scriptId);
    this.displayProgress = true;
    this.progressStatus.text = 'Uploading API file';
    this.progressStatus.isError = false;
    this.processLogs = [];
    this.processLogsDefined = false;
    this.latestPollStatus = '';
    this.setProgressPercentage(5);
    this.restService.APIScriptGenerate(this.routeParams.projectId, formData).subscribe((result: any) => {
      this.apiId = result.data.id;
      this.loadAPIRecordActivities(result.data.id);
    }, (err: any) => {
      this.isGeneratingScripts = false;
      this.displayProgress = false;
      this.progressStatus.text = err?.message || 'Error occurred while uploading API file';
      this.progressStatus.isError = true;
    });
  }
  

  loadAPIRecordActivities(llmScriptId: string) {
    this.progressStatus.text = 'Processing';
    this.setProgressPercentage(Math.min(this.progressStatus.percentage + 10, 20));
    this.isLoading = true;
    this.treeData[0].children = [];
    interval(5000)
      .pipe(
        switchMap(() =>
          this.restService.getApiLLMScriptStatus(
            this.routeParams.projectId,
            llmScriptId
          )
        ),
        takeUntil(this.stopPolling$),
        takeUntil(this.destroy$),
        catchError((error) => {
          console.error(
            "An error occurred while loading recorded activities:",
            error
          );
          this.isLoading = false;
          this.isDataLoaded = false;
          this.toaster.pop({ type: 'error', text: MessageEnum.RECORDED_ACTIVITIES_LOAD_FAILURE });
          return EMPTY;
        })
      )
      .subscribe((res: any) => {
        this.latestPollStatus = res?.data?.status || '';
        this.updateProcessLogs(res?.data?.logs);
        this.updateRecording(res?.data);
        if (
          res.status === 201 &&
          res.data?.status !== 'COMPLETED'
        ) {
          if (res.data.status === 'ERROR') {
            this.stopPolling$.next();
            this.clearProcessLogsUI();
            this.isLoading = false;
            this.openSwaggerValidationError(res.data.errorMessage || 'An error occurred while processing the API file.');
          } else {
            this.progressStatus.isError = false;
            this.setProgressPercentage(Math.min(this.progressStatus.percentage + 20, 90));
            this.progressStatus.text = this.mapProcessStatus(res.data.status) || 'Processing';
          }
        } else {
          this.stopPolling$.next();
          this.progressStatus.text = 'Process completed';
          this.setProgressPercentage(100);

          // Hide generation logs before opening validation / closing agentic flow
          this.clearProcessLogsUI();

          this.isLoading = false;

          if(this.isAgentic) {
            this.activeModal.close('Load the recorded activities');
            return;
          }

          this.responseCopy = res.data?.response;
          let responseData = res.data?.response;

          // this.treeData = [
          //   {
          //     id: "schemas",
          //     name: "Schemas",
          //     displayName: "Schemas",
          //     icon: "dashboard",
          //     order: 1,
          //     level: 0,
          //     nodeType: "schemas",
          //     isTickable: false,
          //     isTicked: false,
          //     children: []
          //   },
          //   {
          //     id: "services",
          //     name: "Services",
          //     displayName: "Services",
          //     icon: "dashboard",
          //     order: 1,
          //     level: 0,
          //     nodeType: "services",
          //     isTickable: false,
          //     isTicked: false,
          //     children: []
          //   }
          // ];

          // responseData?.schemas.forEach((ele: any, ind: number) => {
          //   this.treeData[0].children.push({
          //     ...ele.schema,
          //     displayName: ele.schema.name,
          //     icon: "sprint",
          //     order: ind + 1,
          //     level: 3,
          //     nodeType: "schema",
          //     isTickable: true,
          //     children: []
          //   });
          // });

          // responseData?.services.forEach((ele: any, ind: number) => {
          //   this.treeData[1].children.push({
          //     ...ele,
          //     displayName: ele.name,
          //     icon: "sprint",
          //     order: ind + 1,
          //     level: 3,
          //     nodeType: "service",
          //     isTickable: true,
          //     children: [
          //       {
          //         id: ele.id + '_opr',
          //         displayName: 'Operations',
          //         icon: "sprint",
          //         order: ind + 1,
          //         level: 4,
          //         nodeType: "operations",
          //         isTickable: false,
          //         children: ele.operations.map((item: any) => {
          //           return {
          //             ...item,
          //             level: 5,
          //             id: item.id === 'uuid' ? `${ele.id}_${item.name}` : item.id,
          //             displayName: item.name,
          //             nodeType: 'operation',
          //             order: ind + 1,
          //             isTickable: true,
          //             children: [
          //               {
          //                 id: item.id + '_param',
          //                 displayName: 'Parameters',
          //                 icon: "sprint",
          //                 order: ind + 1,
          //                 level: 6,
          //                 nodeType: "parameter",
          //                 isTickable: true,
          //                 value: item.parameters
          //               }
          //             ]
          //           }
          //         })
          //       }
          //     ]
          //   });
          // });

          // this.totalItemsToValidate = 0;
          // this.totalItemsToValidate += responseData.schemas.length;
          // responseData?.services.forEach((ele: any) => {
          //   this.totalItemsToValidate += 1;
          //   if (ele.operations?.length) {
          //     ele.operations.forEach((opr: any) => {
          //       this.totalItemsToValidate += 1;
          //       if (opr.parameters?.length) {
          //         this.totalItemsToValidate += opr.parameters.length;
          //       }
          //     });
          //   }
          // });


            const svTreeNodes = [
            {
              id: 'schemas',
              displayName: 'Schemas',
              count: responseData.schemas.length,
              children: responseData.schemas.map((ele: any) => ({
                id: ele.schema.id,
                displayName: ele.schema.name
              }))
            },
            {
              id: 'services',
              displayName: 'Services',
              count: responseData.services.length,
              children: responseData.services.map((ele: any) => ({
                id: ele.id,
                displayName: ele.name
              }))
            }
          ];

          const nodeDataMap: { [nodeId: string]: ScriptValidationItem[] } = {};

          responseData.schemas.forEach((ele: any) => {
            nodeDataMap[ele.schema.id] = [{
              id: `${ele.schema.id}_attributes`,
              name: 'Attributes',
              validated: false,
              enabled: false,
              expanded: false,
              fields: (ele.schema.attributes || []).map((attr: any) => ({ name: attr.name, dataType: attr.dataType }))
            }];
          });

          responseData.services.forEach((service: any) => {
            nodeDataMap[service.id] = (service.operations || []).map((op: any) => this.buildOperationItem(op, service.id));
          });

          this.hideParentModal();
          const svModalRef = this.modalService.open(ScriptValidationComponent, {
            centered: true,
            backdrop: 'static',
            windowClass: 'scrip-mapping-modal'
          });
          svModalRef.componentInstance.type = 'api';
          svModalRef.componentInstance.treeNodes = svTreeNodes;
          svModalRef.componentInstance.nodeDataMap = nodeDataMap;

          svModalRef.result.then(() => {
            this.showSavingProgress();
            this.isDataLoaded = true;
            this.saveApiValidations();
          }, () => {
            this.activeModal.dismiss();
          });


        }
      });
  }

  private buildOperationItem(op: any, serviceId: string): ScriptValidationItem {
    return {
      id: op.id === 'uuid' ? `${serviceId}_${op.name}` : op.id,
      name: op.name,
      validated: false,
      enabled: false,
      expanded: false,
      fields: (op.parameters || []).map((p: any) => ({ name: p.name, dataType: p.type }))
    };
  }

  getParams(arr: any) {
    return arr.map();
  }

  saveApiValidations() {
    // this.responseCopy.schemas.forEach((schema: any) => {
    //   let editedSchema = this.treeData[0].children.find((ele: any) => ele.id === schema.schema.id);
    //   schema.schema.attributes = editedSchema.attributes;
    // });


    // this.responseCopy.services.forEach((service: any) => {
    //   let editedService = this.treeData[1].children.find((ele: any) => ele.id === service.id);
    //   service.name = editedService.name;
    //   service.apiDefinitionId = editedService.apiDefinitionId;
    //   service.url = editedService.url;

    //   service.operations.forEach((operation: any) => {
    //     let editedOperation = editedService.children[0].children.find((ele: any) => (operation.id === 'uuid' ? `${service.id}_${operation.name}` : operation.id) === ele.id);
    //     operation.name = editedOperation.name;
    //     operation.type = editedOperation.type,
    //       operation.acceptType = editedOperation.acceptType;
    //     operation.complexReturnType = editedOperation.complexReturnType;
    //     operation.returnType = editedOperation.returnType;
    //     operation.url = editedOperation.url;
    //     operation.parameters = editedOperation.children[0].value.map((param: any) => {
    //       return {
    //         name: param.name,
    //         type: param.type,
    //         paramType: param.paramType,
    //         contentType: param.contentType,
    //         payloadType: param.payloadType
    //       }
    //     });
    //   });
    // });

    this.restService.saveApiLLMValidation(this.routeParams.projectId, this.apiId, this.responseCopy).subscribe((res: any) => {
      this.activeModal.close(res.data);
    }, (error: any) => {
      console.error(error?.message);
    });
  }

  getFilteredDevices() {
    // return this.meta.devices?.filter((ele: any) => ele.environment.toLowerCase() === this.mobileData.type.toLowerCase());
    return this.meta.devices;
  }

  getApps() {
    return this.meta.application?.filter((ele: any) => ele.paradigm?.toLowerCase() === 'mobile');
  }

  /** Clamps progress to 0–100 so the bar never exceeds 100%. */
  private setProgressPercentage(value: number): void {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) {
      this.progressStatus.percentage = 0;
      return;
    }
    this.progressStatus.percentage = Math.max(0, Math.min(100, Math.round(numeric)));
  }

  /** Sync live process logs from task poll response. */
  private updateProcessLogs(logs: any): void {
    if (!Array.isArray(logs)) {
      // undefined / non-array: leave current state as-is
      return;
    }
    // Only flip defined once we receive a non-empty logs array
    if (logs.length > 0) {
      this.processLogsDefined = true;
      this.processLogs = logs.map((log: any) => ({
        step: log?.step,
        thinking: log?.thinking,
        evaluation: log?.evaluation,
      }));
      return;
    }
    // Empty array after logs were already shown — ignore
    if (this.processLogsDefined) {
      return;
    }
  }

  /** Stop the status poll, disconnect the recording, cancel the task, and close. */
  cancelEventStream(): void {
    this.stopPolling$.next();
    this.endRecordingStream(true);
    const recordId = this.scriptRecordId;
    const projectId = this.routeParams?.projectId;
    const close = () => {
      this.isLoading = false;
      this.isGeneratingScripts = false;
      this.progressStatus.text = 'Cancelled';
      this.progressStatus.isError = false;
      this.activeModal.dismiss('cancel');
    };
    if (!this.isManualScriptGeneration || !projectId || !recordId) {
      close();
      return;
    }
    this.restService.cancelAgenticLlmTsg(projectId, recordId).subscribe({
      next: () => close(),
      error: () => close(),
    });
  }

  get isManualScriptGeneration(): boolean {
    return this.scriptGeneratorMode.toUpperCase() === 'MANUAL';
  }

  /** Hide process-logs UI so it does not linger under nested validation modals. */
  private clearProcessLogsUI(): void {
    this.displayProgress = false;
    this.processLogs = [];
    this.processLogsDefined = false;
    this.latestPollStatus = '';
    this.endRecordingStream(true);
    this.scriptRecordId = '';
    this.scriptTaskId = '';
    this.scriptRunStatus = '';
    this.scriptGeneratorMode = '';
  }

  private applyScriptGeneratorMode(mode: unknown): void {
    if (typeof mode !== 'string' || !mode.trim()) {
      return;
    }
    this.scriptGeneratorMode = mode.trim();
  }

  private updateRecording(data: any): void {
    this.applyScriptGeneratorMode(data?.scriptGeneratorMode);
    const recordId = typeof data?.id === 'string' ? data.id.trim() : '';
    if (recordId) {
      this.scriptRecordId = recordId;
    }
    const taskId = typeof data?.taskId === 'string' ? data.taskId.trim() : '';
    const taskJustArrived = !!taskId && taskId !== this.scriptTaskId;
    if (taskId) {
      this.scriptTaskId = taskId;
    }
    if (taskJustArrived) {
      this.clearRecordingRetry();
      this.recordingHold = false;
    }
    if (typeof data?.status === 'string' && data.status.trim()) {
      this.scriptRunStatus = data.status.trim();
    }
    if (!this.isManualScriptGeneration) {
      this.endRecordingStream(true);
      return;
    }
    if (!this.isActiveRecordingStatus()) {
      this.recordingHold = false;
      this.endRecordingStream(false);
      return;
    }
    if (this.scriptTaskId) {
      this.openRecordingStream();
    }
  }

  /** MANUAL runs can stream only while the recorder is pending or processing. */
  private isActiveRecordingStatus(): boolean {
    return this.scriptRunStatus === 'PENDING_Agentic_LLM_TSG'
      || this.scriptRunStatus === 'PROCESSING_Agentic_LLM_TSG';
  }

  /**
   * Open the multipart recording once. Later polls leave that connection alone.
   * A 400 that the recording has not started does not switch the id to taskId.
   */
  private openRecordingStream(): void {
    const projectId = this.routeParams?.projectId;
    if (this.recordingAbort || this.recordingHold) {
      return;
    }
    if (!projectId || !this.scriptRecordId || !this.scriptTaskId || !this.isManualScriptGeneration || !this.isActiveRecordingStatus()) {
      return;
    }
    const token = this.recordingToken;
    const abort = new AbortController();
    this.recordingAbort = abort;
    this.recordingSub = this.restService
      .streamAgenticLlmTsgVideo(projectId, this.scriptRecordId, abort.signal)
      .subscribe({
        next: (frame) => {
          if (token !== this.recordingToken) {
            return;
          }
          this.ngZone.run(() => this.showRecordingFrame(frame));
        },
        error: (error: unknown) => {
          if (token !== this.recordingToken) {
            return;
          }
          this.ngZone.run(() => {
            this.recordingAbort = undefined;
            this.recordingSub = undefined;
            const status = (error as { status?: number })?.status;
            const message = error instanceof Error ? error.message : '';
            // rest.service.ts tags both "has not started" and "is not active" bodies
            // with this name; check it instead of re-matching just one of the phrases.
            const notStarted = (error as { name?: string })?.name === 'RecordingInactiveError'
              || message.includes('has not started');
            const stillLive = this.isActiveRecordingStatus();
            if (stillLive && (status === 404 || notStarted)) {
              this.scheduleRecordingRetry();
              return;
            }
            this.recordingHold = true;
          });
        },
        complete: () => {
          if (token !== this.recordingToken) {
            return;
          }
          this.ngZone.run(() => {
            this.recordingAbort = undefined;
            this.recordingSub = undefined;
          });
        },
      });
  }

  /**
   * Recorder has no frames yet (session not started on the backend). Retry almost
   * right away instead of waiting out the 5s status-poll cadence — each attempt is
   * already paced by its own network round trip, so this does not hammer the
   * backend any harder than the previous flat 10s wait did, it just stops adding
   * extra idle time on top of a failed call.
   */
  private scheduleRecordingRetry(): void {
    this.clearRecordingRetry();
    this.recordingHold = true;
    this.recordingRetry = setTimeout(() => {
      this.recordingRetry = undefined;
      this.recordingHold = false;
      if (this.isActiveRecordingStatus()) {
        this.openRecordingStream();
      }
    }, 300);
  }

  private clearRecordingRetry(): void {
    if (this.recordingRetry) {
      clearTimeout(this.recordingRetry);
      this.recordingRetry = undefined;
    }
  }

  private showRecordingFrame(frame: Blob): void {
    if (!this.recordingAbort || !(frame instanceof Blob) || frame.size === 0) {
      return;
    }
    if (this.recordingUrl) {
      this.retiredRecordingUrls.push(this.recordingUrl);
    }
    const nextUrl = URL.createObjectURL(frame);
    this.recordingObjectUrl = nextUrl;
    this.recordingUrl = nextUrl;
  }

  /** Drop object URLs from frames the image element is no longer showing. */
  releasePreviousRecording(): void {
    const current = this.recordingUrl;
    for (const url of this.retiredRecordingUrls) {
      if (url !== current) {
        URL.revokeObjectURL(url);
      }
    }
    this.retiredRecordingUrls = [];
  }

  /**
   * Abort the open recording. The last frame stays until `clearFrame` is set,
   * which happens when the user leaves the video view, cancels, or the modal closes.
   */
  private endRecordingStream(clearFrame: boolean): void {
    this.clearRecordingRetry();
    this.recordingToken++;
    const abort = this.recordingAbort;
    const sub = this.recordingSub;
    this.recordingAbort = undefined;
    this.recordingSub = undefined;
    sub?.unsubscribe();
    abort?.abort();
    if (!clearFrame) {
      return;
    }
    this.releasePreviousRecording();
    if (this.recordingObjectUrl) {
      URL.revokeObjectURL(this.recordingObjectUrl);
      this.recordingObjectUrl = '';
    }
    this.recordingUrl = '';
  }

  get isProcessActive(): boolean {
    return this.progressStatus.percentage < 100 && !this.progressStatus.isError;
  }

  /**
   * Footer status label:
   * - Before logs populate → mapped poll `status`
   * - Once logs populate → "Thinking..."
   * - Done / error → Completed / Failed
   */
  get processStatusLabel(): string {
    if (this.progressStatus.percentage >= 100 && !this.progressStatus.isError) {
      return 'Completed';
    }
    if (this.progressStatus.isError) {
      return 'Failed';
    }
    if (this.hasPopulatedProcessLogs) {
      return 'Thinking...';
    }
    return this.mapProcessStatus(this.latestPollStatus)
      || this.progressStatus.text
      || 'Initializing';
  }

  private get hasPopulatedProcessLogs(): boolean {
    return this.processLogsDefined
      && this.processLogs.some((log) => !!(log?.thinking || log?.evaluation));
  }

  /** Map API status codes (e.g. PROCESSING_AUTOMATIC_SG) to readable labels. */
  private mapProcessStatus(status: string | null | undefined): string {
    if (!status) {
      return '';
    }
    const raw = String(status).trim();
    if (!raw) {
      return '';
    }
    if (this.constants[raw]) {
      return this.constants[raw];
    }
    const upper = raw.toUpperCase();
    if (this.constants[upper]) {
      return this.constants[upper];
    }
    return raw
      .split(/_+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  getTitle() {
    let title = '';
    if(this.type === 'web') {
      title = 'Automatic Web Test Script Generation';
    } else if(this.type === 'api') {
      title = 'Generate API test script';
    } else if(this.type === 'mobile') {
      title = 'Automatic Mobile Test Script Generation';
    } else if(this.type === 'ai') {
      title = 'Automatic Web Test Script Generation';
    } else {
      title = 'Validate Recorded Activities';
    }
    return title;
  }

}
